import React from 'react';
import authManager from '../../utils/authManager';
import Layout from '../../components/user/Layout';
import { formatDateTime, getFileUrl } from '../../utils/helper';

function Profile() {
  const user = authManager.getUserProfile();

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4">
        <div className="max-w-6xl mx-auto space-y-10">

          <HeaderCard user={user} />

          <div className="grid md:grid-cols-2 gap-8">
            <InfoSection
              title="Contact Details"
              gradient="from-blue-500 to-indigo-600"
              items={[
                { label: 'Primary Contact', value: user?.contact_no },
                { label: 'Alternate Contact', value: user?.contact_no_two },
                { label: 'Address', value: user?.address },
              ]}
            />

            <InfoSection
              title="Professional Info"
              gradient="from-purple-500 to-pink-600"
              items={[
                { label: 'Department', value: user?.department },
                { label: 'Union Code', value: user?.union_code },
                { label: 'Date of Joining', value: formatDateTime(user?.doj) },
              ]}
            />

            <InfoSection
              title="Personal Info"
              gradient="from-green-500 to-teal-600"
              items={[
                { label: 'Date of Birth', value: formatDateTime(user?.dob) },
                { label: 'Blood Group', value: user?.blood_group },
                { label: 'Father Name', value: user?.father_name },
              ]}
            />

            <InfoSection
              title="Identity & Address Info"
              gradient="from-cyan-500 to-blue-500"
              items={[
                { label: 'Aadhar Number', value: user?.aadhar_no },
                { label: 'PAN Number', value: user?.pancard_no },
                { label: 'State', value: user?.state },
                { label: 'District', value: user?.district },
                { label: 'City', value: user?.city },
              ]}
            />

            <InfoSection
              title="Bank Details"
              gradient="from-yellow-500 to-orange-500"
              items={[
                { label: 'Bank Name', value: user?.bank_name },
                { label: 'Branch', value: user?.branch },
                { label: 'Account No', value: user?.account_no },
                { label: 'IFSC Code', value: user?.ifsc_code },
                { label: 'Account Holder Name', value: user?.account_holder_name },
                { label: 'Account Type', value: user?.account_type },
              ]}
            />

            <InfoSection
              title="Nominee Details"
              gradient="from-pink-500 to-rose-600"
              items={[
                { label: 'Nominee Name', value: user?.nominee_name },
                { label: 'Relationship', value: user?.nominee_relationship },
                { label: 'Aadhar Details', value: user?.nominee_aadhar_details },
                { label: 'Contact No', value: user?.nominee_contact_no },
              ]}
            />
          </div>

          {/* Family Members Section */}
          {user?.sons?.length > 0 && <FamilySection user={user} />}
        </div>
      </div>
    </Layout>
  );
}

/* ---------- Subcomponents ---------- */

const HeaderCard = ({ user }) => (
  <div className="relative rounded-3xl shadow-2xl overflow-hidden transform hover:scale-[1.01] transition-transform duration-500">
    <div
      className="h-48 relative overflow-hidden"
      style={{
        background: `linear-gradient(90deg, ${user?.gradient_color || '#6366F1'}, #9333EA)`,
      }}
    >
      <div className="absolute inset-0 bg-black/10"></div>
      <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-white/10 rounded-full blur-2xl"></div>
      <div className="absolute -top-20 -left-20 w-64 h-64 bg-white/10 rounded-full blur-2xl"></div>
    </div>

    <div className="relative px-8 pb-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between">
        <div className="flex flex-col md:flex-row items-center md:items-end gap-6 -mt-16">
          {user?.image ? (
            <img
              src={getFileUrl(user.image, 'members')}
              alt="Profile"
              className="w-32 h-32 rounded-2xl object-cover border-4 border-white shadow-xl ring-4 ring-white/50"
            />
          ) : (
            <div className="w-32 h-32 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center text-5xl font-bold border-4 border-white shadow-xl ring-4 ring-blue-100">
              {user?.name?.charAt(0) || 'U'}
            </div>
          )}
          <div className="text-center md:text-left mb-4 md:mb-0">
            <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight animate-fadeIn">
              {user?.name || 'User'}
            </h1>
            <p className="text-gray-600 flex items-center justify-center md:justify-start gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              {user?.email || 'N/A'}
            </p>
          </div>
        </div>
        <div className="flex justify-center md:justify-end mt-4 md:mt-0">
          <span
            className={`px-6 py-2 rounded-full text-sm font-semibold shadow-lg ${
              user?.status === 'Active'
                ? 'bg-green-100 text-green-700 ring-2 ring-green-300 animate-pulse'
                : 'bg-red-100 text-red-700 ring-2 ring-red-300'
            }`}
          >
            ● {user?.status}
          </span>
        </div>
      </div>
    </div>
  </div>
);

const InfoSection = ({ title, gradient, items }) => (
  <div className="bg-white/80 backdrop-blur-md rounded-3xl shadow-lg p-8 hover:shadow-2xl transition-shadow duration-300">
    <div className="flex items-center gap-3 mb-6">
      <div className={`w-12 h-12 bg-gradient-to-br ${gradient} rounded-xl flex items-center justify-center shadow-lg`}>
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
    </div>
    <div className="space-y-4">
      {items.map((item, idx) => (
        <InfoCard key={idx} label={item.label} value={item.value} />
      ))}
    </div>
  </div>
);

const InfoCard = ({ label, value }) => (
  <div className="flex items-start gap-4 p-4 bg-white/70 backdrop-blur-md rounded-xl border border-gray-200 hover:border-blue-300 transition-all duration-300 hover:shadow-md">
    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 text-white rounded-lg flex items-center justify-center shadow-sm">
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
      </svg>
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-gray-500 mb-1">{label}</p>
      <p className="text-base font-semibold text-gray-800 break-words">{value || '—'}</p>
    </div>
  </div>
);

const FamilySection = ({ user }) => (
  <div className="bg-white/70 backdrop-blur-md rounded-3xl shadow-xl p-8">
    <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-pink-600 rounded-xl flex items-center justify-center shadow-md">
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5V8H2v12h5m10 0a2 2 0 01-2-2V10a2 2 0 012-2h2a2 2 0 012 2v8a2 2 0 01-2 2z" />
        </svg>
      </div>
      Family Members
    </h2>
    <div className="flex gap-6 overflow-x-auto pb-4">
      {user?.sons?.map((son) => (
        <div
          key={son.id}
          className="flex-shrink-0 w-56 bg-gradient-to-br from-indigo-50 to-purple-100 rounded-2xl shadow-md p-4 hover:shadow-xl transition-all duration-300"
        >
          {son.document ? (
            <img
              src={getFileUrl(son.document, 'sons')}
              alt={son.name}
              className="w-16 h-16 rounded-full object-cover mx-auto mb-3 shadow-md border-2 border-white"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center text-lg font-bold mx-auto mb-3">
              {son.name?.charAt(0)}
            </div>
          )}
          <p className="text-center text-lg font-semibold text-gray-700">{son.name}</p>
          <p className="text-center text-sm text-gray-500 mt-1">Aadhar: {son.aadhar_no}</p>
          <p className="text-center text-sm text-gray-500">{son.type}</p>
        </div>
      ))}
    </div>
  </div>
);

export default Profile;
